import { Fragment, useCallback, useMemo, useState } from 'react';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import FiltersForeignStateList from 'system/geographic/components/state/list';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { useEffect } from 'react';
import { ForeignState } from 'shell/models/state';
import { FiltersFormForeignState, ForeignStateClient } from './types';
import { useFormik } from 'formik';
import { transformDataset } from './utils';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesForeignState } from '..';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { useNavigate } from 'react-router-dom';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonForeignStateCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import ListHeader from 'shell/components/shared/list-header';
import { headCells, validationSchemaStateFilter, authsObj } from './constants';
import { Typography } from '@mui/material';

const ForeignStateListPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<ForeignStateClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<ForeignStateClient>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormForeignState>();
  const {
    data,
    get: getForeignStateList,
    error,
  } = useHttpFetch<HttpResponseList<ForeignState>>(`${__MUNICIPALITY_API_URL__}/geo/stato`, {
    data: {
      payload: {
        list: [],
      },
    },
  });

  const { delete: deleteForeignState } = useHttpFetch<HttpResponse<ForeignState>>(
    `${__MUNICIPALITY_API_URL__}/geo/stato`
  );

  const foreignStateList: Array<ForeignStateClient> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list.map((foreignState) => ({
      id: foreignState.key.codice,
      code: foreignState.codIstat,
      acronym: foreignState.key.codice,
      name: foreignState.nome,
    }));
  }, [data]);

  const onSubmit = useCallback(
    (formValues: FiltersFormForeignState) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterForeignStateForm.setFieldValue('page', 0);
      getForeignStateList(queryString);
    },
    [getForeignStateList]
  );

  const filterForeignStateForm = useFormik({
    initialValues: initialValuesForeignState,
    validationSchema: validationSchemaStateFilter,
    onSubmit,
  });
  useEffect(() => {
    filterForeignStateForm.setValues(filterTable.values || initialValuesForeignState);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterForeignStateForm.values);
  }, [filterForeignStateForm.values]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload) {
      return 0;
    }
    return data.payload.list.length;
  }, [error, data?.payload]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterForeignStateForm.values, orders };
      const queryString = transformDataset(newDataset);
      return getForeignStateList(queryString).then(() => {
        filterForeignStateForm.setFieldValue('orders', orders);
      });
    },
    [filterForeignStateForm.values, getForeignStateList, filterForeignStateForm.setFieldValue]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<ForeignStateClient> => {
      return new Promise((resolve) => {
        navigate(`/sistema/geografiche/estero/stato/dettaglio/${cell.id}`);
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<ForeignStateClient> => {
      return new Promise((resolve) => {
        navigate(`/sistema/geografiche/estero/stato/modifica/${cell.id}`);
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<ForeignStateClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<ForeignStateClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (page) => {
      filterForeignStateForm.setFieldValue('page', page || 0);
    },
    [filterForeignStateForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterForeignStateForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      filterForeignStateForm.setValues(newDataset);
    },
    [filterForeignStateForm.setFieldValue, filterForeignStateForm.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonForeignStateCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo stato"
        addBtnLabel="Aggiungi nuovo stato"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/geografiche/estero/stato/nuovo');
        }}
        title="Gestione Stati Esteri"
      />
      <div className="pmi-my-3">
        <FiltersForeignStateList filterForeignStateForm={filterForeignStateForm} />
      </div>
      {data?.payload ? (
        !(foreignStateList.length > 0) ? (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        ) : (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterForeignStateForm.values.orders}
            activeActions={auths}
            page={filterForeignStateForm.values.page}
            rowsPerPage={filterForeignStateForm.values.rowsPerPage}
            body={foreignStateList}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Stati Esteri"
          />
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Stato Estero"
        message={`Confermare la cancellazione del stato ${deleteAlertModal.element?.name || ''}?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          deleteForeignState(`/${deleteAlertModal.element?.id}`).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Stato Estero"
        message={`La cancellazione del stato ${
          deleteSuccessModal.element?.name || ''
        } è avvenuta con successo`}
        onClose={() => {
          const queryString = transformDataset(filterForeignStateForm.values);
          getForeignStateList(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
        onOk={() => {
          const queryString = transformDataset(filterForeignStateForm.values);
          getForeignStateList(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
      />
    </Fragment>
  );
};

ForeignStateListPage.displayName = 'Foreign State List page component';

export default ForeignStateListPage;
